import React, { memo, useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { createUseStyles, useTheme, Icon, Popover, Text } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { CopilotContext } from '../../../Context';
import { DEFAULT_INPUT_MODE, INPUT_MODE } from '../../../hooks/constants';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotBarInputMode');

const CopilotBarInputMode = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useWeb5Context();
    const classes = useStyles();
    const theme = useTheme();
    const { copilotBarProps, setCopilotBarProps } = useContext(CopilotContext);

    const selectedMode = copilotBarProps?.inputMode ?? DEFAULT_INPUT_MODE;

    const handleVisibleChange = useCallback((visible) => {
        setIsOpen(visible);
    }, []);

    const handleMenuItemClick = useCallback(
        (mode) => {
            // Explicitly clear inputPlaceholder to ensure CopilotBar derives the correct
            // default placeholder for the new mode, removing any previous overrides.
            setCopilotBarProps({ inputMode: mode, inputPlaceholder: null });
            handleVisibleChange(false);
        },
        [handleVisibleChange, setCopilotBarProps],
    );

    const menuItems = useMemo(
        () => [
            {
                icon: 'forms',
                mode: INPUT_MODE.TEXT,
                title: t('label_copilot_voicecrud_fill_with_text_or_voice'),
            },
            {
                icon: 'attachFileVertical',
                mode: INPUT_MODE.FILE,
                title: t('label_copilot_voicecrud_fill_from_a_file'),
            },
            {
                icon: 'world',
                mode: INPUT_MODE.URL,
                title: t('label_copilot_voicecrud_fill_from_a_url'),
            },
        ],
        [t],
    );

    const popoverContent = useMemo(
        () => (
            <div className={classes.menuContent}>
                {menuItems.map((item) => (
                    <div
                        key={item.mode}
                        className={classes.menuItem}
                        onClick={() => handleMenuItemClick(item.mode)}
                    >
                        <Icon name={item.icon} color={theme.colors.grey[500]} />
                        <Text color={theme.colors.utility.textPrimary}>{item.title}</Text>
                    </div>
                ))}
            </div>
        ),
        [
            classes.menuContent,
            classes.menuItem,
            handleMenuItemClick,
            menuItems,
            theme.colors.grey,
            theme.colors.utility.textPrimary,
        ],
    );

    const selectedIcon = useMemo(
        () => menuItems.find((item) => item.mode === selectedMode)?.icon ?? 'forms',
        [menuItems, selectedMode],
    );

    const triggerElement = useMemo(
        () => (
            <span className={classNames(classes.root, { [classes.open]: isOpen })}>
                <Icon name={selectedIcon} color="currentColor" />
                <Icon name={isOpen ? 'arrowDropUp' : 'arrowDropDown'} color="currentColor" />
            </span>
        ),
        [classes.open, classes.root, isOpen, selectedIcon],
    );

    const popoverProps = useMemo(
        () => ({
            className: classes.popover,
            content: popoverContent,
            onVisibleChange: handleVisibleChange,
            placement: 'topRight',
            visible: isOpen,
        }),
        [classes.popover, isOpen, handleVisibleChange, popoverContent],
    );

    return <Popover {...popoverProps}>{triggerElement}</Popover>;
};

const MemoizedCopilotBarInputMode = memo(CopilotBarInputMode);
MemoizedCopilotBarInputMode.displayName = 'CopilotBarInputMode';

export default MemoizedCopilotBarInputMode;
